import { Droplets, Leaf, LineChart, Thermometer } from 'lucide-react';
import type { DegreeDayParams, GenericInfectionParams, PestModel, PestModelInput, PestModelType } from '../../types/pest';

/**
 * Shared by the pest pages and PestModelForm. Kept out of the component file
 * so fast refresh keeps working on it.
 */

// Compact revised-Mills default so new infection pests work out of the box.
const DEFAULT_MILLS = [
  { t: 4, light: 26, moderate: 34, severe: 44 }, { t: 8, light: 15, moderate: 20, severe: 30 },
  { t: 10, light: 13, moderate: 17, severe: 26 }, { t: 16, light: 9, moderate: 12, severe: 19 },
  { t: 20, light: 9, moderate: 12, severe: 19 }, { t: 25, light: 11, moderate: 14, severe: 21 },
];

export function defaultParams(type: PestModelType): Record<string, unknown> {
  if (type === 'degree_day') {
    return { base_temp_c: 7.5, upper_temp_c: 31, season: { start: { month: 8, day: 1 }, end: { month: 5, day: 31 } },
      events: [{ name: 'Egg hatch', dd: 120, risk: 'moderate', action: '' }] } as DegreeDayParams as unknown as Record<string, unknown>;
  }
  if (type === 'infection_wetness') {
    return { wetness_source: 'auto', rh_threshold_pct: 90, min_temp_c: 6, max_temp_c: 30, dry_break_hours: 4,
      season: { start: { month: 8, day: 1 }, end: { month: 3, day: 31 } }, mills_table: DEFAULT_MILLS } as unknown as Record<string, unknown>;
  }
  if (type === 'generic_infection') {
    return { t_min_c: 5, t_opt_c: 20, t_max_c: 30, w_min_h: 6, w_max_h: 40, dry_break_hours: 4, rh_threshold_pct: 90,
      wetness_source: 'auto', season: { start: { month: 8, day: 1 }, end: { month: 5, day: 31 } } } as GenericInfectionParams as unknown as Record<string, unknown>;
  }
  return { opt_temp_min: 15, opt_temp_max: 30, rh_threshold_pct: 80, bands: { low: 1, moderate: 25, high: 50, severe: 75 } };
}

export const TYPE_ICON: Record<PestModelType, React.ReactNode> = {
  degree_day: <Thermometer className="h-4 w-4" />,
  infection_wetness: <Leaf className="h-4 w-4" />,
  generic_infection: <Droplets className="h-4 w-4" />,
  risk_index: <LineChart className="h-4 w-4" />,
};

/** Everything the form edits — a saved model, a blank, or an AI proposal. */
export type PestDraft = PestModelInput;

export function draftFromModel(m: PestModel): PestDraft {
  return {
    name: m.name, common_name: m.common_name, scientific_name: m.scientific_name, kind: m.kind,
    description: m.description, model_type: m.model_type, model_params: m.model_params,
    recommended_actions: m.recommended_actions, host_crop_type_ids: m.host_crop_type_ids,
    is_active: m.is_active, profile: m.profile,
  };
}

export const BLANK_DRAFT: PestDraft = {
  name: '', common_name: null, scientific_name: null, kind: 'disease', description: null,
  model_type: 'generic_infection', model_params: defaultParams('generic_infection'),
  recommended_actions: null, host_crop_type_ids: [], is_active: true, profile: null,
};
